import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTimeFormat, getLocalDateTime } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { ServerSideDataTable } from 'src/common/components/dataTable/serverSide';
import { DatatableColumn, FilterDataType } from 'src/common/components/dataTable/types';
import { LoadingProgress } from 'src/common/store/types';
import { PagedRequest, PagedResult } from 'src/store';
import { ScheduledEvent } from '../../store/types';
import { DeleteScheduledEventModal } from './DeleteScheduledEventModal';

interface Props {
  pagedRequest: PagedRequest;
  handlePagedRequest: (pagedRequest: PagedRequest) => void;
  fetchScheduledEventListProgress: LoadingProgress;
  deleteScheduledEventProgress: LoadingProgress;
  deleteScheduledEvent: (id: string) => void;
  paginatedEventListResults: PagedResult<ScheduledEvent>;
}

export const ReportingSchedulerTable = (props: Props): JSX.Element => {
  const history = useHistory();
  const [deletingRow, setDeletingRow] = useState<ScheduledEvent | null>(null);

  const columns: DatatableColumn[] = useMemo(
    () => [
      {
        name: 'name',
        label: 'Name',
        options: {
          sort: true,
          filter: true,
        },
      },
      {
        name: 'description',
        label: 'Description',
        options: {
          sort: true,
          filter: true,
        },
      },
      {
        name: 'occurrences',
        label: 'Occurrences',
        filterDataType: FilterDataType.numeric,
        options: {
          sort: true,
          filter: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            return row.occurrences && row.occurrences > 0 ? `${row.occurrences}` : '';
          },
        },
      },
      {
        name: 'recurrenceType',
        label: 'Recurrence Type',
        options: {
          sort: true,
          filter: true,
        },
      },
      {
        name: 'startOn',
        label: 'Started on*',
        filterDataType: FilterDataType.date,
        options: {
          sort: true,
          filter: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.startOn, DateTimeFormat.DayDate);
          },
        },
      },
      {
        name: 'endOn',
        label: 'Ends on*',
        filterDataType: FilterDataType.date,
        options: {
          sort: true,
          filter: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.endOn, DateTimeFormat.DayDate);
          },
        },
      },
      {
        name: 'nextRunOn',
        label: 'Next run on*',
        filterDataType: FilterDataType.date,
        options: {
          sort: true,
          filter: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.nextRunOn, DateTimeFormat.DayDate);
          },
        },
      },
      {
        name: 'calculatedRemainingInstances',
        label: 'Remaining Occurrences',
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            if (row.recurrenceType === 'Once') {
              return 'One-time event (does not repeat).';
            }
            if (row.calculatedRemainingInstances && row.calculatedRemainingInstances > 0) {
              return `${row.calculatedRemainingInstances} remaining occurrence(s). Final run on ${getLocalDateTime(
                row.calculatedFinalRunOn,
                DateTimeFormat.DayDate
              )}.`;
            }
            return 'No remaining occurrences.';
          },
        },
      },
      {
        name: 'createdOn',
        label: 'Created on',
        filterDataType: FilterDataType.date,
        options: {
          sort: true,
          filter: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return '';
            return getLocalDateTime(row.createdOn, DateTimeFormat.DayDate);
          },
        },
      },
      {
        name: '',
        label: '',
        options: {
          sort: false,
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = props.paginatedEventListResults?.results[dataIndex];
            if (!row) return null;
            return (
              <Box display="flex">
                <Tooltip title="Edit">
                  <span>
                    <IconButton color="primary" onClick={() => history.replace(`/scheduler/edit/${row.id}`)}>
                      <Edit />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton color="primary" onClick={() => setDeletingRow(row)}>
                      <Delete />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          },
        },
      },
    ],
    [props.paginatedEventListResults]
  );

  const tableOptions: MUIDataTableOptions = {
    filter: true,
    search: false,
    viewColumns: true,
    download: false,
    print: false,
    selectableRows: 'none',
    selectableRowsHideCheckboxes: true,
    pagination: true,
    serverSide: true,
    customToolbar: () => (
      <Tooltip title="Create">
        <WO2Button onClick={() => history.replace('/scheduler/create')} startIcon={<Add />}>
          Create
        </WO2Button>
      </Tooltip>
    ),
  };

  return (
    <>
      <ServerSideDataTable<ScheduledEvent>
        title="* Dates are in Sydney time zone"
        pagedRequest={props.pagedRequest}
        handlePagedRequest={props.handlePagedRequest}
        pagedResult={props.paginatedEventListResults}
        loadingProgress={props.fetchScheduledEventListProgress}
        columns={columns}
        options={tableOptions}
      />

      {deletingRow && (
        <DeleteScheduledEventModal
          isOpen={deletingRow !== null}
          handleClose={() => setDeletingRow(null)}
          deleteScheduledEvent={props.deleteScheduledEvent}
          deleteScheduledEventProgress={props.deleteScheduledEventProgress}
          row={deletingRow}
        />
      )}
    </>
  );
};
